import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { withForm, ValidatedInput, SubmitButton } from '../../util/FormHelpers';
import { types, resetPasswordRequest } from 'legacy/actions/userActions';
import {
  isRequired,
  isEmail,
  composeValidators
} from '../../../util/validators';
import { BaseFormProps } from '../types';

import styles from './ResetPasswordRequest.module.scss';
import { ModalInput } from '../ModalUtils';

interface ResetPasswordFormData {
  email: string;
}

interface ResetPasswordFormProps {
  loading: boolean;
  resetPasswordRequest: (email: string) => void;
  onSuccess: () => void;
  formProps: BaseFormProps<ResetPasswordFormData>;
  resetPasswordError: { message?: string };
}


const ResetPasswordForm = ({
  loading,
  resetPasswordRequest,
  onSuccess,
  formProps,
  resetPasswordError
}: ResetPasswordFormProps) => {
  const [hasSubmitted, setHasSubmitted] = useState(false);

  useEffect(() => {
    if (
      !loading &&
      hasSubmitted &&
      !formProps.submitting &&
      !formProps.hasSubmitErrors &&
      formProps.valid
    ) {
      onSuccess();
    }
  }, [loading, hasSubmitted, formProps, onSuccess]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setHasSubmitted(true);
    formProps.handleSubmit();
    if (formProps.valid || formProps.dirtySinceLastSubmit) {
      resetPasswordRequest(formProps.values.email);
    }
  };

  const getErrorMessage = () => {
    const { hasSubmitErrors, submitErrors } = formProps;
    if (!hasSubmitErrors) {
      return null;
    }
    if (resetPasswordError.message) {
      return resetPasswordError.message;
    }
    const { FORM_ERROR = {} } = submitErrors;
    const { error = {} } = FORM_ERROR as { error?: { message?: string } };
    const { message } = error as { message?: string };
    if (message) {
      return message;
    }

    switch (submitErrors.FORM_ERROR) {
      case 'User not found':
        return 'No registered user has that e-mail.';
      default:
        return 'An error has occurred, please try again.';
    }
  };

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      <div className={styles.title}>Forgot Your Password?</div>
      <div className="img-wrapper">
        <img src="/img/illustrations/forgotPassword.svg" alt="" />
      </div>
      <label htmlFor="email">Please enter your account email</label>
      <ValidatedInput
        type="text"
        validate={composeValidators(isEmail, isRequired) as any}
        name="email"
        placeholder="you@example.com"
        component={ModalInput}
      />
      <div className="fill-vertical" />

      <SubmitButton
        buttonText="Send password reset email"
        loading={loading}
        errorOpen={
          formProps.hasSubmitErrors &&
          !formProps.dirtySinceLastSubmit &&
          !formProps.submitting
        }
        errorContent={getErrorMessage()}
        onSuccess={undefined}
        type="primary"
        className="submit"
        errorActionText="Go back"
        disabled={false}
      />
    </form>
  );
};

function mapStateToProps(state: any) {
  return {
    loading: state.user.loading.RESET_PASSWORD_REQUEST,
    resetPasswordError: state.user.errors.RESET_PASSWORD_REQUEST
  };
}

const actions = { resetPasswordRequest };

export default connect(
  mapStateToProps,
  actions
)(withForm((types as any).RESET_PASSWORD_REQUEST_FORM)(ResetPasswordForm));
